import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "@/components/ui/modal";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { UnsplashAttribution } from "@/components/ui/UnsplashAttribution";
import { MediaItem, normalizeMedia } from "@/types/media.types";

const imageCache = new Map<string, Promise<void>>();

const preloadImage = (src: string): Promise<void> => {
  if (!src) return Promise.resolve();

  const cached = imageCache.get(src);
  if (cached) return cached;

  const promise = new Promise<void>((resolve) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });

  imageCache.set(src, promise);
  return promise;
};

interface MediaCarouselModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Accetta anche il formato legacy (array di URL). */
  media?: MediaItem[] | string[];
  initialIndex?: number;
}

export const MediaCarouselModal: React.FC<MediaCarouselModalProps> = ({
  isOpen,
  onClose,
  media: rawMedia = [],
  initialIndex = 0,
}) => {
  const media = useMemo(() => normalizeMedia(rawMedia), [rawMedia]);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [displayedSrc, setDisplayedSrc] = useState("");

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  useEffect(() => {
    if (!isOpen || !media.length) return;

    const currentSrc = media[currentIndex]?.url;
    const prevSrc = media[(currentIndex - 1 + media.length) % media.length]?.url;
    const nextSrc = media[(currentIndex + 1) % media.length]?.url;

    let active = true;

    preloadImage(currentSrc).then(() => {
      if (active) setDisplayedSrc(currentSrc);
    });

    // Preload neighbor images to make navigation instant.
    preloadImage(prevSrc);
    preloadImage(nextSrc);

    return () => {
      active = false;
    };
  }, [isOpen, media, currentIndex]);

  const prev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + media.length) % media.length);
  }, [media.length]);

  const next = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % media.length);
  }, [media.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next]);

  if (!media.length) return null;

  const currentItem = media[currentIndex];

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" showHeader={false} size="fit">
      <div className="relative flex items-center justify-center p-6 select-none">
        {/* Prev */}
        {media.length > 1 && (
          <button
            onClick={prev}
            className="absolute left-3 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {/*
          Wrapper inline-block: si stringe sull'immagine renderizzata, così la banda
          di attribuzione resta agganciata al bordo della foto e non alla larghezza
          del contenitore (che è più ampio per via del padding e delle frecce).
          `block` sull'img elimina il gap del baseline inline, che altrimenti
          farebbe fluttuare la banda qualche pixel sotto la foto.
        */}
        <div className="relative inline-block">
          <img
            src={displayedSrc || currentItem?.url}
            alt={currentItem?.alt || `Media ${currentIndex + 1}`}
            className="block max-h-[85vh] object-contain rounded-lg"
            loading="eager"
          />
          <UnsplashAttribution item={currentItem} className="rounded-b-lg px-2 py-1 text-[11px]" />
        </div>

        {/* Next */}
        {media.length > 1 && (
          <button
            onClick={next}
            className="absolute right-3 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Dots */}
      {media.length > 1 && (
        <div className="flex justify-center gap-1.5 py-2">
          {media.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentIndex ? "bg-primary" : "bg-gray-300"
              }`}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};
